import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getBlogBySlug } from "../../../../api/blogs";
import { isBlogCoverFallback, resolveBlogCoverSrc } from "../../../../utils/blogImages";
import { resolveBlogPdfUrl, blogPdfFileName } from "../../../../utils/blogPdf";
import { sanitizeBlogHtml } from "../../../../utils/blogHtml";
import { estimateReadingMinutes } from "../../../../utils/readingTime";
import { isBlogPublished } from "../../../../utils/blogStatus";
import { localizeBlog } from "../../../../utils/blogLocales";
import useLangLink from "../../../../utils/useLangLink";
import SEO from "../../../../utils/SEO";
import { getLanguageMeta } from "../../../../i18n/languages";
import "./PublicBlogDetail.css";
import { FaArrowLeft } from "react-icons/fa";

const PublicBlogDetail = () => {
    const { t, i18n } = useTranslation();
    const lp = useLangLink();
    const tp = useCallback(
        (k, d) => t(`pages.blog.${k}`, d != null ? { defaultValue: d } : undefined),
        [t]
    );
    const lang = (i18n.language || "en").split("-")[0];

    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        const fetchBlog = async () => {
            setLoading(true);
            setError("");
            setBlog(null);
            try {
                const data = await getBlogBySlug(slug, lang);
                if (cancelled) return;

                if (!data || !isBlogPublished(data)) {
                    setError(tp("blogNotFoundDesc"));
                } else {
                    setBlog(localizeBlog(data, lang));
                }
            } catch (err) {
                if (cancelled) return;
                const status = err?.response?.status;
                const msg =
                    status === 404
                        ? tp("blogNotFoundDesc")
                        : err?.response?.data?.message ||
                          err?.message ||
                          tp("errorLoadingDesc");
                setError(msg);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchBlog();

        return () => {
            cancelled = true;
        };
    }, [slug, lang, tp]);

    const imageSrc = useMemo(() => resolveBlogCoverSrc(blog?.image), [blog?.image]);
    const coverIsFallback = useMemo(() => isBlogCoverFallback(blog?.image), [blog?.image]);
    const pdfSrc = useMemo(() => resolveBlogPdfUrl(blog?.pdfUrl), [blog?.pdfUrl]);

    const createdLabel = useMemo(() => {
        if (!blog?.createdAt) return "";
        const date = new Date(blog.createdAt);
        if (Number.isNaN(date.getTime())) return "";
        return date.toLocaleDateString(getLanguageMeta(i18n.language).htmlLang, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    }, [blog?.createdAt, i18n.language]);

    const readingTime = useMemo(() => {
        const minutes =
            typeof blog?.readingTimeMinutes === "number" && blog.readingTimeMinutes >= 1
                ? Math.round(blog.readingTimeMinutes)
                : estimateReadingMinutes(blog?.content || "");
        return `${minutes} ${tp("minutesRead")}`;
    }, [blog, tp]);

    const authorLabel = useMemo(() => {
        return String(blog?.author || tp("defaultAuthor")).trim();
    }, [blog?.author, tp]);

    const displayTitle = useMemo(() => {
        return String(blog?.title || tp("defaultTitle")).trim();
    }, [blog?.title, tp]);

    const seoTitle = useMemo(() => {
        const custom = String(blog?.metaTitle || "").trim();
        return custom || displayTitle;
    }, [blog?.metaTitle, displayTitle]);

    const seoDescription = useMemo(() => {
        const custom = String(blog?.metaDescription || "").trim();
        return custom || String(blog?.intro || "").trim();
    }, [blog?.metaDescription, blog?.intro]);

    const seoKeywords = useMemo(() => String(blog?.metaKeywords || "").trim(), [blog?.metaKeywords]);

    const seoUrl = useMemo(() => {
        const slugValue = blog?.slug || slug;
        return slugValue ? `/blogs/${slugValue}` : "/blogs";
    }, [blog?.slug, slug]);

    if (loading) {
        return (
            <main className="main">
                <div className="section pb-section container">
                    <section className="pb" aria-label={tp("aria.detailsLoading")}>
                        <div className="pb-topbar-skeleton" />
                        <div className="pb-hero-skeleton" />
                        <div className="pb-panel-skeleton" />
                    </section>
                </div>
            </main>
        );
    }

    if (error) {
        return (
            <main className="main">
                <div className="section pb-section container">
                    <section className="pb" aria-label={tp("aria.detailsError")}>
                        <div className="pb-state error" role="alert">
                            <h2 className="pb-state-title">{tp("couldNotLoadBlog")}</h2>
                            <p className="pb-state-text">{error}</p>
                            <Link className="pb-link-back" to={lp("/blogs")}>
                                <FaArrowLeft /> {tp("backToList")}
                            </Link>
                        </div>
                    </section>
                </div>
            </main>
        );
    }

    if (!blog) {
        return (
            <main className="main">
                <div className="section pb-section container">
                    <section className="pb" aria-label={tp("aria.detailsEmpty")}>
                        <div className="pb-state">
                            <h2 className="pb-state-title">{tp("blogNotFound")}</h2>
                            <p className="pb-state-text">{tp("blogNotFoundDesc")}</p>
                            <Link className="pb-link-back" to={lp("/blogs")}>
                                {tp("backToList")}
                            </Link>
                        </div>
                    </section>
                </div>
            </main>
        );
    }

    return (
        <main className="main">
            <SEO
                title={seoTitle}
                description={seoDescription}
                keywords={seoKeywords || undefined}
                author={authorLabel}
                url={seoUrl}
                type="article"
            />
            <div className="section pb-section container">
                <section className="pb" aria-label={tp("aria.details")}>
                    <div className="pb-topbar">
                        <Link to={lp("/blogs")} className="pb-link-back">
                            <FaArrowLeft /> {tp("backToList")}
                        </Link>
                    </div>

                    <article className="pb-article">
                        <header className="pb-hero">
                            <img
                                className={`pb-hero-image${coverIsFallback ? " pb-hero-image--fallback" : ""}`}
                                src={imageSrc}
                                alt={coverIsFallback ? "Offshore Advisory" : displayTitle || tp("coverAlt")}
                            />

                            <div className="pb-hero-overlay" />

                            <div className="pb-hero-content">
                                <h1 className="pb-title">{displayTitle}</h1>

                                <div className="pb-meta-row">
                                    <span className="pb-chip">
                                        {tp("by")} {authorLabel}
                                    </span>
                                    {createdLabel ? <span className="pb-chip">{createdLabel}</span> : null}
                                    <span className="pb-chip">{readingTime}</span>
                                </div>
                            </div>
                        </header>

                        {blog?.intro ? (
                            <section className="pb-intro" aria-label={tp("aria.intro")}>
                                <p className="pb-intro-text">{blog.intro}</p>
                            </section>
                        ) : null}

                        {pdfSrc ? (
                            <section className="pb-pdf pb-pdf--name-only" aria-label={tp("aria.pdf")}>
                                <a
                                    className="pb-pdf-name-link"
                                    href={pdfSrc}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {blogPdfFileName(blog?.pdfUrl)}
                                </a>
                            </section>
                        ) : null}

                        <section className="pb-content-wrap" aria-label={tp("aria.content")}>
                            <div
                                className="pb-content"
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeBlogHtml(blog?.content || ""),
                                }}
                            />
                        </section>
                    </article>
                </section>
            </div>
        </main>
    );
};

export default PublicBlogDetail;
